import { videoFavoriteState, videoLikedState } from 'constant/video'
import { useCallback, useEffect, useRef, useState, useContext, useMemo, createContext } from 'react'
import CoverImage from '../CoverImage'
import InfoWrapper from '../InfoWrapper'
import Toolbar from '../Toolbar'
import { StyledCoverImage, StyledShortPlayer, StyledTouchEffect, StyledUploaderInfo, StyledVirtualPage } from './Styled'
import PromotionInfo from '../PromotionInfo'
import { useSelector } from 'react-redux'
import { selectTopicList } from 'redux/selector/app'
import { fetchPostAdEvent } from 'api'
import { formatDate } from 'utils/timeFormat'

// ---- Player control registry (Context) ----
// 讓祖先層級可用 key 直接呼叫 player 控制器
interface PlayerControlContextValue {
  register: (key: any, api: any) => () => void
  get: (key: any) => any
  remove: (key: any) => void
}

const PlayerControlContext = createContext<PlayerControlContextValue | null>(null)

interface PlayerControlProviderProps {
  children: React.ReactNode
}

export function PlayerControlProvider({ children }: PlayerControlProviderProps) {
  const storeRef = useRef(new Map())

  const register = useCallback((key: any, api: any) => {
    storeRef.current.set(key, api)
    return () => storeRef.current.delete(key)
  }, [])

  const get = useCallback((key: any) => {
    return storeRef.current.get(key)
  }, [])

  const remove = useCallback((key: any) => {
    storeRef.current.delete(key)
  }, [])

  const value = useMemo(() => ({ register, get, remove }), [register, get, remove])

  return <PlayerControlContext.Provider value={value}>{children}</PlayerControlContext.Provider>
}

export function usePlayerControl(key: any) {
  const ctx = useContext(PlayerControlContext)

  const pause = useCallback(() => {
    ctx?.get(key)?.pause()
  }, [ctx, key])

  const play = useCallback(() => {
    ctx?.get(key)?.play()
  }, [ctx, key])

  const currentTime = useCallback(
    (second: number) => {
      ctx?.get(key)?.currentTime(second)
    },
    [ctx, key]
  )

  const remove = useCallback(
    (key: any) => {
      ctx?.remove(key)
    },
    [ctx]
  )

  const setAutoplay = useCallback(
    (bool: boolean) => {
      ctx?.get(key)?.setAutoplay(bool)
    },
    [ctx, key]
  )

  return { pause, play, currentTime, remove, setAutoplay }
}
// ---- /Player control registry (Context) ----

interface PlayerPageProps {
  mode?: 'video' | 'promotion'
  videoInfo?: any
  onBase64ImageLoad?: any
  posterSrc?: string | null
  onLike?: () => void
  onSave?: () => void
  promoteInfo?: any
  autoplay?: boolean
  preloadedImages?: any[]
}

const PlayerPage = ({
  mode = 'video',
  videoInfo: videoInfoProp,
  onBase64ImageLoad,
  posterSrc = null,
  onLike,
  onSave,
  promoteInfo,
  autoplay = false,
  preloadedImages = [],
}: PlayerPageProps) => {
  const playerControlCtx = useContext(PlayerControlContext)
  const playerRef = useRef<any>()

  const [videoInfo, setVideoInfo] = useState<any>(videoInfoProp)

  // 註冊到 Context：祖先層級可以用 usePlayerControl(controlKey) 直接控制
  useEffect(() => {
    if (mode !== 'video') return
    if (!playerControlCtx) return
    if (videoInfo?.id == null) return

    const unregister = playerControlCtx.register(videoInfo?.id, {
      pause: () => {
        playerRef.current?.pause()
      },
      play: () => {
        playerRef.current?.play()
      },
      currentTime: (second: number) => {
        playerRef.current?.currentTime(second)
      },
      setAutoplay: (bool: boolean) => {
        playerRef.current?.setAutoplay(bool)
      },
    })

    return unregister
  }, [playerControlCtx, videoInfo?.id, mode])

  if (videoInfoProp != null && videoInfoProp !== videoInfo) {
    setVideoInfo(videoInfoProp)
  }

  const onDoubleCLick = (ev: MouseEvent) => {
    const spawnEffect = touchEffectRef.current?.spawnEffect
    const container: HTMLDivElement | undefined = touchEffectRef.current?.el
    if (container && spawnEffect) {
      const basePosition = container.getBoundingClientRect()
      const { x, y } = basePosition
      const startRotation = Math.PI * (Math.random() * 2 - 1) * 0.1
      const waveRotation = Math.PI * (Math.random() * 2 - 1) * 0.2
      const offsetX = (Math.random() - 0.5) * 50
      const offsetY = (Math.random() - 0.5) * 50
      const startScale = 0
      const endScale = 1.1 + Math.random() * 0.1
      spawnEffect(
        ev.clientX - x + offsetX,
        ev.clientY - y + offsetY,
        startRotation,
        startRotation + waveRotation,
        startScale,
        endScale
      )
    }

    if (videoInfo.like !== videoLikedState.TRUE) {
      onLike?.()
    }
  }
  const touchEffectRef = useRef<any>()

  /**
   * on promotion page click
   */
  const onClick = useCallback(() => {
    if (promoteInfo) {
      fetchPostAdEvent({
        date: formatDate(new Date()),
        material_id: promoteInfo.id,
        material_name: promoteInfo.name,
        oholo_site_id: promoteInfo.site_type,
        oholoer_id: promoteInfo.oholoer_id,
        oholoer_link: promoteInfo.oholoer_link,
        oholoer_name: promoteInfo.oholoer_name,
      })
      window.open(promoteInfo?.link, '_blank')
    }
  }, [promoteInfo])

  const topicList = useSelector(selectTopicList)

  let topics: any[] = []

  if (mode === 'video' && videoInfo) {
    topics =
      videoInfo.topic_ids
        ?.map((id: any) => {
          const topicItem = topicList.find((t: any) => t.id === id)
          if (topicItem) {
            return {
              text: '#' + topicItem.name,
              id: topicItem.id,
              params: {
                topic_ids: String(topicItem.id),
              },
            }
          } else {
            return null
          }
        })
        .filter((text: any) => text != null) ?? []
  }

  const coverImageItemMain =
    videoInfoProp == null
      ? []
      : posterSrc
      ? [
          <StyledCoverImage
            imageStyle={{ objectFit: 'contain' }}
            imageWrapperStyle={{ background: 'black' }}
            key={'loaded-' + videoInfoProp.coverphoto_v}
            src={posterSrc}
            onBase64ImageLoad={onBase64ImageLoad}
            base64SrcSource={videoInfoProp.coverphoto_v}
          />,
        ]
      : [
          <StyledCoverImage
            imageStyle={{ objectFit: 'contain' }}
            imageWrapperStyle={{ background: 'black' }}
            key={videoInfo?.coverphoto_v}
            onBase64ImageLoad={onBase64ImageLoad}
            base64SrcSource={videoInfo?.coverphoto_v}
          />,
        ]

  const currentCoverKey = videoInfoProp?.coverphoto_v ?? videoInfo?.coverphoto_v
  const safePreloadedImages = Array.isArray(preloadedImages) ? preloadedImages : []
  const preloadedImagesFiltered = safePreloadedImages.filter((i) => i.key === currentCoverKey)

  const coverArray = [
    ...(preloadedImagesFiltered && preloadedImagesFiltered.length > 0
      ? preloadedImagesFiltered.map((i: any) => (
          <StyledCoverImage
            key={'loaded-' + i.key}
            src={i.value}
            base64SrcSource={i.key}
            imageStyle={{ objectFit: 'contain' }}
            imageWrapperStyle={{ background: 'black' }}
          />
        ))
      : coverImageItemMain),
  ]

  return (
    <StyledVirtualPage>
      {videoInfo && (
        <InfoWrapper style={mode === 'video' ? {} : { display: 'none' }}>
          {coverArray}
          <StyledShortPlayer ref={playerRef} videoInfo={videoInfo} onDoubleCLick={onDoubleCLick} autoplay={autoplay} />
          <StyledUploaderInfo
            topics={topics}
            uploader={'@' + videoInfo.uploader_name}
            uploaderId={videoInfo.uploader_id}
            videoName={videoInfo.title}
          />
          <StyledTouchEffect ref={touchEffectRef} />
        </InfoWrapper>
      )}
      {mode === 'video' && videoInfo && (
        <Toolbar
          key={videoInfo.id}
          liked={videoInfo.like === videoLikedState.TRUE}
          saved={videoInfo.favorite === videoFavoriteState.TRUE}
          likeCount={videoInfo.like_count}
          saveCount={videoInfo.favorite_count}
          onLike={onLike}
          onSave={onSave}
        ></Toolbar>
      )}
      {mode !== 'video' && promoteInfo && (
        <InfoWrapper onClick={onClick}>
          <CoverImage
            src={posterSrc}
            onBase64ImageLoad={onBase64ImageLoad}
            base64SrcSource={promoteInfo.icon_path}
            source={'picture'}
            style={{ pointerEvents: 'none' }}
          />
          <PromotionInfo promoter="@官方推荐" description={promoteInfo?.name} link={promoteInfo?.link} />
        </InfoWrapper>
      )}
    </StyledVirtualPage>
  )
}

export default PlayerPage
